import { LinearGradient } from 'expo-linear-gradient';
import { useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, Dimensions, Image, Modal, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import Logo from '../assets/images/logo.svg';
import { REDESIGN_COLORS } from '../styles/colors';
import DesktopHeader from '../components/common/DesktopHeader.js';
import MembershipSection from '../components/common/MembershipSection.js';
import ScreenHeader from '../components/common/ScreenHeader.js';
import Footer from '../components/landing/sections/Footer.js';
import { landingPageContent } from '../constants/Content.js';
import { useAuth } from '../context/AuthContext';
import { useNavLinks } from '../hooks/useNavLinks';
import { useSentryScreenTracking } from '../hooks/useSentryPerformance.js';
import DatabaseService from '../utils/databaseService';
import { useFonts } from '../utils/fontLoader.js';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';
import { trackConversion } from '../utils/gtmHelpers';
import { isPushSupported, getPushPermissionState, subscribeToPush, unsubscribeFromPush, hasDevicePushSubscription, isIOSSafari, isStandaloneMode } from '../services/pushService';


const BREAKPOINT_WIDTH = 768;

// Date-only; no time-of-day (changed per design 2026-04-20).
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const ProfileScreen = ({ bottomInset = 0 }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [screenWidth, setScreenWidth] = useState(Dimensions.get('window').width);
  const [digestOptedIn, setDigestOptedIn] = useState(false);
  const [digestToggling, setDigestToggling] = useState(false);
  const [pushOptedIn, setPushOptedIn] = useState(false);
  const [pushToggling, setPushToggling] = useState(false);
  const [pushPermission, setPushPermission] = useState(() => getPushPermissionState());
  const pushSupported = isPushSupported();
  const iosNeedsInstall = isIOSSafari() && !isStandaloneMode();
  const [showA2HSModal, setShowA2HSModal] = useState(false);
  // Full-name inline edit: no explicit buttons, autosaves on blur / submit.
  const [nameDraft, setNameDraft] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const { user, session, logout } = useAuth();
  // Tier now lives in the Membership section below; no longer referenced here.
  const posthog = usePostHog();
  const router = useRouter();
  const { fontsLoaded } = useFonts();

  // SENTRY PERFORMANCE TRACKING
  useSentryScreenTracking('Profile', true);

  // Unified navigation links (used by DesktopHeader)
  const navLinks = useNavLinks({ logout, session });

  useEffect(() => {
    posthog.capture('profile_screen_viewed', {
      timestamp: new Date().toISOString(),
    });

    const fetchProfileData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        const profileResult = await DatabaseService.getUserProfile(user.id);

        if (profileResult.error) {
          throw profileResult.error;
        }

        const userProfile = {
          ...user, // Basic info from auth
          ...profileResult.data, // Full name, avatar etc. from profiles table
        };

        setProfile(userProfile);
        const initialName = user?.user_metadata?.full_name || '';
        setDisplayName(initialName);
        setNameDraft(initialName);
        setDigestOptedIn(!!profileResult.data?.email_digest_opted_in);
        // Check this device's actual push subscription, not the user-level DB flag
        const deviceHasPush = await hasDevicePushSubscription();
        setPushOptedIn(deviceHasPush);
        posthog.capture('profile_data_loaded', {
          has_full_name: !!userProfile.full_name,
        });
      } catch (e) {
        setError(e.message);
        logger.error('Error fetching profile data:', e.message);
        posthog.capture('profile_load_error', {
          error_message: e.message,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const updateScreenWidth = () => {
      setScreenWidth(Dimensions.get('window').width);
    };
    const subscription = Dimensions.addEventListener('change', updateScreenWidth);
    return () => subscription?.remove();
  }, []);

  // Autosaves on blur / submit. No-op when value unchanged so we don't spam the API.
  const handleCommitName = useCallback(async () => {
    if (!user?.id || nameSaving) return;
    const trimmed = (nameDraft || '').trim();
    if (trimmed === (displayName || '').trim()) return;
    setNameSaving(true);
    try {
      const { error } = await supabase.auth.updateUser({ data: { full_name: trimmed } });
      if (error) throw error;
      setDisplayName(trimmed);
      posthog?.capture('profile_full_name_updated', { has_value: trimmed.length > 0 });
    } catch (err) {
      logger.error('[ProfileScreen] Failed to update full_name:', err);
      // Revert the draft to last-known-good so the input reflects reality.
      setNameDraft(displayName || '');
      setError('Could not save your name. Please try again.');
    } finally {
      setNameSaving(false);
    }
  }, [user?.id, nameSaving, nameDraft, displayName, posthog]);

  const handleDigestToggle = useCallback(
    async (newValue) => {
      if (!user?.id || digestToggling) return;

      setDigestToggling(true);
      const previousValue = digestOptedIn;
      setDigestOptedIn(newValue); // Optimistic update

      try {
        const { error: updateError } = await supabase
          .from('users')
          .update({ email_digest_opted_in: newValue })
          .eq('id', user.id);

        if (updateError) throw updateError;

        posthog.capture('email_digest_toggled', {
          opted_in: newValue,
        });

        if (newValue) {
          trackConversion(
            'email_alerts_subscribed',
            { user_id: user.id, source: 'profile_toggle' },
            { email: profile?.email || user.email }
          );
        }
      } catch (err) {
        logger.error('[ProfileScreen] Failed to update email digest preference:', err);
        setDigestOptedIn(previousValue); // Revert on error
      } finally {
        setDigestToggling(false);
      }
    },
    [user?.id, digestOptedIn, digestToggling, posthog]
  );

  const handlePushToggle = useCallback(
    async (newValue) => {
      if (!user?.id || pushToggling) return;

      setPushToggling(true);
      const previousValue = pushOptedIn;

      try {
        if (newValue) {
          // Subscribing — triggers browser permission prompt
          const result = await subscribeToPush();
          if (result.success) {
            setPushOptedIn(true);
            setPushPermission('granted');
          } else {
            setPushPermission(result.permissionState);
          }
          posthog.capture('push_notification_toggled', {
            opted_in: result.success,
            permission_state: result.permissionState,
          });
        } else {
          // Unsubscribing
          setPushOptedIn(false); // Optimistic
          const success = await unsubscribeFromPush();
          if (!success) {
            setPushOptedIn(previousValue); // Revert
          }
          posthog.capture('push_notification_toggled', {
            opted_in: false,
            permission_state: getPushPermissionState(),
          });
        }
      } catch (err) {
        logger.error('[ProfileScreen] Failed to toggle push notifications:', err);
        setPushOptedIn(previousValue);
      } finally {
        setPushToggling(false);
      }
    },
    [user?.id, pushOptedIn, pushToggling, posthog]
  );

  if (!fontsLoaded) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color={REDESIGN_COLORS.ctaBlue} />
        <Text style={styles.loadingText}>Loading fonts...</Text>
      </View>
    );
  }

  const renderContent = () => {
    if (loading) {
      return (
        <View style={styles.centered}>
          <ActivityIndicator size="large" color={REDESIGN_COLORS.ctaBlue} />
          <Text style={styles.loadingText}>Loading profile...</Text>
        </View>
      );
    }

    if (error) {
      return (
        <View style={styles.centered}>
          <Icon name="alert-circle-outline" size={64} color="#FF6B6B" style={styles.errorIcon} />
          <Text style={styles.errorTitle}>Unable to Load Profile</Text>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      );
    }

    if (!profile) {
      return (
        <View style={styles.centered}>
          <Text style={styles.emptyText}>No profile data found.</Text>
        </View>
      );
    }

    return (
      <View style={styles.profileContent}>
        {/* Page Title */}
        <View style={styles.titleSection}>
          <Text
            style={[styles.pageTitle, screenWidth < BREAKPOINT_WIDTH && styles.pageTitleMobile]}
          >
            My Profile
          </Text>
          <Text style={styles.pageSubtitle}>View and manage your account information</Text>
        </View>

        {/* Account Information Card */}
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Icon name="account-circle" size={24} color={REDESIGN_COLORS.ctaBlue} />
            <Text style={styles.cardTitle}>Account Information</Text>
          </View>
          <View style={styles.cardContent}>
            <View style={styles.infoRow}>
              <Text style={styles.label}>Full Name</Text>
              <View style={styles.nameInputWrap}>
                <TextInput
                  style={styles.nameInput}
                  value={nameDraft}
                  onChangeText={setNameDraft}
                  onBlur={handleCommitName}
                  onSubmitEditing={handleCommitName}
                  placeholder="Add your name"
                  placeholderTextColor={REDESIGN_COLORS.textTertiary}
                  returnKeyType="done"
                  editable={!nameSaving}
                  accessibilityLabel="Full name"
                />
                {nameSaving && (
                  <ActivityIndicator
                    size="small"
                    color={REDESIGN_COLORS.textSecondary}
                    style={styles.nameSpinner}
                  />
                )}
              </View>
            </View>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.label}>Email</Text>
              <Text style={styles.value}>{profile.email}</Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.label}>User ID</Text>
              <Text style={[styles.value, styles.monospace]}>{profile.id}</Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.label}>Member Since</Text>
              <Text style={styles.value}>{formatDate(profile.created_at)}</Text>
            </View>
            <View style={styles.divider} />
            <View style={styles.infoRow}>
              <Text style={styles.label}>Last Sign-in</Text>
              <Text style={styles.value}>{formatDate(profile.last_sign_in_at)}</Text>
            </View>
          </View>
        </View>

        {/* Membership (D-007) */}
        <MembershipSection />

        {/* Notification Preferences Card */}
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Icon name="bell-outline" size={24} color={REDESIGN_COLORS.ctaBlue} />
            <Text style={styles.cardTitle}>Notification Preferences</Text>
          </View>
          <View style={styles.cardContent}>
            <View style={styles.infoRow}>
              <View style={styles.digestToggleInfo}>
                <Text style={styles.label}>Daily Listings Digest</Text>
                <Text style={styles.digestDescription}>
                  Receive a daily email summary of new listings matching your preferences
                </Text>
              </View>
              <Switch
                value={digestOptedIn}
                onValueChange={handleDigestToggle}
                disabled={digestToggling}
                trackColor={{ false: '#D1D5DB', true: 'rgba(59, 130, 246, 0.4)' }}
                thumbColor={digestOptedIn ? '#3B82F6' : '#F4F7FE'}
                activeThumbColor="#3B82F6"
              />
            </View>
            <View style={styles.divider} />
            {iosNeedsInstall ? (
              <Pressable style={styles.infoRow} onPress={() => setShowA2HSModal(true)}>
                <View style={styles.digestToggleInfo}>
                  <Text style={styles.label}>Push Notifications</Text>
                  <Text style={styles.digestDescription}>
                    Tap to learn how to enable push notifications on iOS
                  </Text>
                </View>
                <Icon name="chevron-right" size={22} color={REDESIGN_COLORS.textSecondary} />
              </Pressable>
            ) : (
              <View style={styles.infoRow}>
                <View style={styles.digestToggleInfo}>
                  <Text style={styles.label}>Browser Push Notifications</Text>
                  <Text style={styles.digestDescription}>
                    {!pushSupported
                      ? 'Not supported in this browser'
                      : pushPermission === 'denied'
                        ? 'Blocked by browser. Check your browser notification settings to re-enable'
                        : 'Get instant alerts for price drops, sold listings, and new matches'}
                  </Text>
                </View>
                <Switch
                  value={pushOptedIn}
                  onValueChange={handlePushToggle}
                  disabled={pushToggling || !pushSupported || pushPermission === 'denied'}
                  trackColor={{ false: '#D1D5DB', true: 'rgba(59, 130, 246, 0.4)' }}
                  thumbColor={pushOptedIn ? '#3B82F6' : '#F4F7FE'}
                  activeThumbColor="#3B82F6"
                />
              </View>
            )}
          </View>
        </View>

        {/* Logout Button */}
        <TouchableOpacity style={styles.logoutButton} onPress={logout}>
          <Icon name="logout" size={20} color="#EF4444" />
          <Text style={styles.logoutText}>Log Out</Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <View style={styles.screenContainer}>
      <LinearGradient
        colors={['#0E264D', '#041635', '#020D25', '#040409']}
        locations={[0, 0.101, 0.294, 1]}
        style={StyleSheet.absoluteFill}
      />
      {screenWidth >= BREAKPOINT_WIDTH && (
        <DesktopHeader
          logo={Logo}
          navLinks={navLinks}
          variant="dark"
          onLogoPress={() => router.push('/home')}
        />
      )}
      <View
        style={[
          styles.mainContentContainer,
          screenWidth < BREAKPOINT_WIDTH && styles.mainContentContainerMobile,
        ]}
      >
        {screenWidth < BREAKPOINT_WIDTH && (
          <ScreenHeader title="Settings" subtitle="View and manage your account" />
        )}

        <View style={{ flex: 1 }}>
          <ScrollView
            contentContainerStyle={{
              flexGrow: 1,
              paddingTop: screenWidth < BREAKPOINT_WIDTH ? 0 : 100,
              paddingHorizontal: screenWidth < BREAKPOINT_WIDTH ? 16 : 32,
              alignItems: 'center',
              paddingBottom: (screenWidth < BREAKPOINT_WIDTH ? 20 : 40) + bottomInset,
            }}
          >
            <View style={styles.contentWrapper} data-screen="profile">{renderContent()}</View>

            {screenWidth < BREAKPOINT_WIDTH && <Footer content={landingPageContent.footer} variant="dark" hideLogo />}
          </ScrollView>
          {screenWidth >= BREAKPOINT_WIDTH && (
            <Footer
              content={landingPageContent.footer}
              variant="dark"
              style={{ paddingTop: 16, paddingBottom: 16 }}
              hideLogo
            />
          )}
        </View>
      </View>
      {/* iOS Add to Home Screen modal */}
      <Modal
        visible={showA2HSModal}
        transparent
        animationType="fade"
        onRequestClose={() => setShowA2HSModal(false)}
      >
        <Pressable style={styles.a2hsOverlay} onPress={() => setShowA2HSModal(false)}>
          <Pressable style={styles.a2hsModal} onPress={(e) => e.stopPropagation()}>
            <Pressable style={styles.a2hsClose} onPress={() => setShowA2HSModal(false)} hitSlop={12}>
              <Icon name="close" size={20} color={REDESIGN_COLORS.textSecondary} />
            </Pressable>

            <Image source={require('../../assets/icons/favicon.png')} style={styles.a2hsIcon} />
            <Text style={styles.a2hsTitle}>Enable Push Notifications</Text>
            <Text style={styles.a2hsSubtitle}>
              On iOS, push notifications require DooCars to be installed on your Home Screen.
            </Text>

            <View style={styles.a2hsSteps}>
              <View style={styles.a2hsStep}>
                <View style={styles.a2hsStepNum}><Text style={styles.a2hsStepNumText}>1</Text></View>
                <Text style={styles.a2hsStepText}>
                  Tap the <Text style={styles.a2hsBold}>Share</Text> button <Icon name="export-variant" size={14} color={REDESIGN_COLORS.ctaBlue} /> in Safari
                </Text>
              </View>
              <View style={styles.a2hsStep}>
                <View style={styles.a2hsStepNum}><Text style={styles.a2hsStepNumText}>2</Text></View>
                <Text style={styles.a2hsStepText}>
                  Scroll down, tap <Text style={styles.a2hsBold}>Add to Home Screen</Text> <Icon name="plus-box-outline" size={14} color={REDESIGN_COLORS.ctaBlue} />
                </Text>
              </View>
              <View style={styles.a2hsStep}>
                <View style={styles.a2hsStepNum}><Text style={styles.a2hsStepNumText}>3</Text></View>
                <Text style={styles.a2hsStepText}>
                  Open <Text style={styles.a2hsBold}>DooCars</Text> from your Home Screen and enable notifications
                </Text>
              </View>
            </View>

            <Pressable style={styles.a2hsDismiss} onPress={() => setShowA2HSModal(false)}>
              <Text style={styles.a2hsDismissText}>Got it</Text>
            </Pressable>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  screenContainer: {
    flex: 1,
  },
  mainContentContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: 'transparent',
  },
  mainContentContainerMobile: {
    position: 'relative',
    flex: 1,
    width: '100%',
    margin: 0,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 1024,
  },
  centered: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    minHeight: 400,
  },
  loadingText: {
    fontFamily: 'Satoshi-Regular',
    marginTop: 16,
    fontSize: 16,
    color: REDESIGN_COLORS.textPrimary,
    textAlign: 'center',
  },
  errorIcon: {
    marginBottom: 16,
  },
  errorTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 24,
    color: REDESIGN_COLORS.textPrimary,
    marginBottom: 12,
    textAlign: 'center',
  },
  errorText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    color: '#FF6B6B',
    textAlign: 'center',
    maxWidth: 400,
  },
  emptyText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 18,
    color: REDESIGN_COLORS.textPrimary,
    textAlign: 'center',
  },
  profileContent: {
    width: '100%',
    gap: 24,
  },
  titleSection: {
    alignItems: 'center',
    marginBottom: 16,
  },
  pageTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 32,
    lineHeight: 38.4,
    letterSpacing: -0.32,
    textAlign: 'center',
    color: REDESIGN_COLORS.textPrimary,
    marginBottom: 8,
  },
  pageTitleMobile: {
    fontSize: 24,
    lineHeight: 28.8,
  },
  pageSubtitle: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 16,
    lineHeight: 25.6,
    textAlign: 'center',
    color: REDESIGN_COLORS.textSecondary,
  },
  card: {
    backgroundColor: REDESIGN_COLORS.cardBg,
    borderRadius: 16,
    overflow: 'hidden',
    marginBottom: 24,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    padding: 24,
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.08)',
  },
  cardTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 20,
    color: REDESIGN_COLORS.textPrimary,
    letterSpacing: -0.2,
  },
  cardContent: {
    padding: 24,
  },
  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: 16,
    gap: 16,
  },
  label: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 14,
    color: 'rgba(255, 255, 255, 0.5)',
    flex: 1,
  },
  value: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
    textAlign: 'right',
    flex: 2,
  },
  monospace: {
    fontFamily: 'monospace',
    fontSize: 12,
  },
  nameInputWrap: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
  },
  nameInput: {
    flex: 1,
    textAlign: 'right',
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
    paddingVertical: 4,
    // Hairline on focus via outline; input reads as text until touched.
    borderWidth: 0,
    backgroundColor: 'transparent',
    // @ts-ignore RN Web: suppress default outline so it blends with the row.
    outlineStyle: 'none',
  },
  nameSpinner: {
    marginLeft: 4,
  },
  divider: {
    height: 1,
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  preferenceItem: {
    paddingVertical: 16,
    gap: 8,
  },
  questionText: {
    fontFamily: 'Satoshi-Medium',
    fontSize: 16,
    color: REDESIGN_COLORS.textPrimary,
    lineHeight: 24,
  },
  answerText: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: REDESIGN_COLORS.textSecondary,
    lineHeight: 22.4,
  },
  tierBadgeContainer: {
    flex: 2,
    alignItems: 'flex-end',
  },
  tierBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
  },
  tierBadgeText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  // Free tier
  tierBadgeFree: {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
  },
  tierBadgeTextFree: {
    color: 'rgba(255, 255, 255, 0.5)',
  },
  // Pro tier
  tierBadgePro: {
    backgroundColor: 'rgba(20, 76, 184, 0.12)',
  },
  tierBadgeTextPro: {
    color: '#144CB8',
  },
  // Premium tier
  tierBadgePremium: {
    backgroundColor: 'rgba(147, 51, 234, 0.12)',
  },
  tierBadgeTextPremium: {
    color: '#9333EA',
  },
  // Admin tier
  tierBadgeAdmin: {
    backgroundColor: 'rgba(239, 68, 68, 0.12)',
  },
  tierBadgeTextAdmin: {
    color: '#EF4444',
  },
  // Email digest toggle
  digestToggleInfo: {
    flex: 1,
    gap: 4,
  },
  digestDescription: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 12,
    color: 'rgba(255, 255, 255, 0.4)',
    lineHeight: 18,
  },
  logoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: 'rgba(239, 68, 68, 0.3)',
    backgroundColor: 'rgba(239, 68, 68, 0.05)',
  },
  logoutText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 16,
    color: '#EF4444',
  },

  // A2HS Modal
  a2hsOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 24,
  },
  a2hsModal: {
    backgroundColor: REDESIGN_COLORS.cardBg,
    borderRadius: 20,
    padding: 28,
    width: '100%',
    maxWidth: 380,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: REDESIGN_COLORS.borderSubtle,
  },
  a2hsClose: {
    position: 'absolute',
    top: 14,
    right: 14,
    zIndex: 1,
  },
  a2hsIcon: {
    width: 56,
    height: 56,
    borderRadius: 14,
    marginBottom: 16,
  },
  a2hsTitle: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 18,
    color: REDESIGN_COLORS.textPrimary,
    marginBottom: 6,
  },
  a2hsSubtitle: {
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: REDESIGN_COLORS.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  a2hsSteps: {
    gap: 14,
    width: '100%',
    marginBottom: 24,
  },
  a2hsStep: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  a2hsStepNum: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: 'rgba(5, 86, 247, 0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  a2hsStepNumText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 12,
    color: REDESIGN_COLORS.ctaBlue,
  },
  a2hsStepText: {
    flex: 1,
    fontFamily: 'Satoshi-Regular',
    fontSize: 14,
    color: REDESIGN_COLORS.textPrimary,
    lineHeight: 20,
  },
  a2hsBold: {
    fontFamily: 'Satoshi-Bold',
  },
  a2hsDismiss: {
    backgroundColor: REDESIGN_COLORS.ctaBlue,
    borderRadius: 12,
    height: 44,
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  a2hsDismissText: {
    fontFamily: 'Satoshi-Bold',
    fontSize: 15,
    color: '#FFFFFF',
  },
});

export default ProfileScreen;
